import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Form from '@nineeng/forms';
import { useState } from 'react';
import { Tabs, UserImportResult, type ImportResult } from '@repo/components';
import { Role } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { importUsers, importBlocklist } from 'src/api/admin/system';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
export const Route = createFileRoute('/_admin/system/import')({
	loader: async () => {
		const roles = await getRolesForAutocomplete();
		return { roles };
	},
	component: ImportPage,
});

function ImportPage() {
	const { roles: initialRoles } = Route.useLoaderData();

	const { data: roles } = useQuery({
		queryKey: ['roles-for-import'],
		queryFn: () => getRolesForAutocomplete(),
		initialData: initialRoles,
	});

	const [data, setData] = useState<Record<string, any>>({});
	const [importResult, setImportResult] = useState<ImportResult | null>(null);
	const [tab, setTab] = useState(0);
	const [processing, setProcessing] = useState(false);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Import');

	const UsersTab = () => {
		const form = {
			sections: [
				{
					type: 'SettingsSection',
					fields: [
						{
							name: 'roleIds',
							rowLabel: 'Roles',
							rowDescription: 'Roles to assign to the imported users',
							type: 'AutoCompleteByID',
							multiple: true,
							options: roles.map((r) => ({ ...r, id: r.id })),
							getOptionLabel: (o: Role) => o.name,
						},
						{
							name: 'groupId',
							rowLabel: 'Company',
							rowDescription: 'Company to assign to the imported users',
							type: 'AsyncAutoComplete',
							dataSource: '/api/data-sources/groups/data',
						},
						{
							name: 'sendNotification',
							rowLabel: 'Send invites',
							rowDescription: 'When checked, Users will receive an email invite on creation',
							type: 'Checkbox',
						},
						{
							rowLabel: 'Upload file',
							rowDescription: (
								<>
									Template file:{' '}
									<a href="/api/system/import-template/users" download style={{ textDecoration: 'underline' }}>
										ImportTemplate_User.xlsx
									</a>
								</>
							),
							name: 'file',
							type: 'Upload',
							fileType: 'system',
							dropzone: true,
							accept: {
								'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
							},
						},
						importResult?.success === false && {
							type: 'Custom',
							Component: () => <UserImportResult importResult={importResult} />,
						},
						{
							type: 'Custom',
							Component: Button,
							variant: 'outlined',
							children: 'Import users',
							size: 'medium',
							onClick: () => handleImportUsers(),
							disabled: !data.file || processing,
						},
					].filter(Boolean),
				},
			],
		};

		const handleImportUsers = async () => {
			setProcessing(true);
			try {
				const { body: result } = await importUsers(data);
				setImportResult(result as ImportResult);
				setData({});
				if ((result as ImportResult).success) {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Finished importing users',
					});
				} else {
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to import some users',
					});
				}
			} catch (_e) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error importing users',
				});
			}
			setProcessing(false);
		};

		return (
			<>
				<Alert severity="info" sx={{ marginBottom: 2 }}>
					When the email or phone number of an imported user matches an already existing user, both will be merged into
					the already existing user.
				</Alert>
				<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
			</>
		);
	};

	const BlocklistTab = () => {
		const form = {
			sections: [
				{
					type: 'SettingsSection',
					fields: [
						{
							name: 'replacePrevImported',
							rowLabel: 'Replace all from previous import',
							rowDescription:
								'When checked, the import will first clear the existing blocklist (with the exception of manually added records)',
							type: 'Checkbox',
						},
						{
							rowLabel: 'Upload file',
							rowDescription: (
								<>
									Template file: <a href="/public/ImportTemplate_Blocklist.xlsx" download>ImportTemplate_Blocklist.xlsx</a>
								</>
							),
							name: 'file',
							type: 'Upload',
							fileType: 'system',
							dropzone: true,
							accept: {
								'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
							},
						},
						{
							type: 'Custom',
							Component: Button,
							variant: 'outlined',
							children: 'Import blocklist',
							size: 'medium',
							onClick: () => handleImportBlocklist(),
							disabled: !data.file || processing,
						},
					],
				},
			],
		};

		const handleImportBlocklist = async () => {
			setProcessing(true);
			try {
				await importBlocklist(data);
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Finished importing blocklist',
				});
				setData({});
			} catch (_e) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error importing blocklist',
				});
			}
			setProcessing(false);
		};

		return <Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />;
	};

	const onChange = (formData: Record<string, any>) => {
		setData(formData);
	};

	const tabs = [
		{ label: 'Users', el: <UsersTab /> },
		{ label: 'Blocklist', el: <BlocklistTab /> },
	];

	return (
		<div className="h-full w-full p-8">
			<div className="flex mb-3">
				<Typography variant="h4">Import Data</Typography>
			</div>
			<Paper variant="outlined">
				<Tabs value={tab} onChange={(value) => setTab(value)}>
					<Tabs.TabList>
						{tabs.map((t, index) => (
							<Tabs.Tab key={t.label} value={index} label={t.label} />
						))}
					</Tabs.TabList>
					{tabs.map((t, index) => (
						<Tabs.TabPanel key={t.label} value={index} selectedValue={tab}>
							<div>{t.el}</div>
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
